import React from "react";

/* ─── Temi cliente ─── */
const THEMES = {
  saponeria:   { primary: "#00A651", secondary: "#39B54A" },
  acquasapone: { primary: "#0066CC", secondary: "#004499" },
  default:     { primary: "#7F77DD", secondary: "#5b21b6" },
};
function getTheme(n) {
  const s = (n || "").toLowerCase();
  if (s.includes("saponeria"))  return THEMES.saponeria;
  if (s.includes("acqua"))      return THEMES.acquasapone;
  return THEMES.default;
}

/* ─── Formatter ─── */
function fmtEur(n) {
  return "€" + (n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmt(n) { return (n || 0).toLocaleString("it-IT"); }
function fmtPct(v) {
  if (!v && v !== 0) return "—";
  return v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "%";
}

/* ─── Platform helpers ─── */
const PLATFORM_COLORS = { google: "#639922", meta: "#378ADD", tiktok: "#888780" };
function getPlatformColor(name) {
  const s = (name || "").toLowerCase();
  if (s.includes("meta") || s.includes("facebook") || s.includes("instagram")) return PLATFORM_COLORS.meta;
  if (s.includes("tiktok")) return PLATFORM_COLORS.tiktok;
  return PLATFORM_COLORS.google;
}
function getPlatformLabel(name) {
  const s = (name || "").toLowerCase();
  if (s.includes("meta") || s.includes("facebook") || s.includes("instagram")) return "Meta";
  if (s.includes("tiktok")) return "TikTok";
  return "Google";
}

/* ─── KPI resolver ─── */
// Mappa una KPI key sul valore calcolato dai campaignInsights aggregati
function resolveKpi(key, ins) {
  if (!ins) return null;
  const spend        = ins.spend              || 0;
  const impr         = ins.impressions        || 0;
  const clicks       = ins.inline_link_clicks || ins.clicks || 0; // inline_link_clicks è primario
  const reach        = ins.reach              || 0;
  const reactions    = ins.post_reactions     || 0;
  // engagement: usa il valore reale se disponibile, altrimenti post_reactions come proxy
  const engagement   = (ins.engagement > 0) ? ins.engagement : reactions;

  switch (key) {
    case 'spend':       return spend > 0       ? { value: fmtEur(spend),               raw: spend }    : null;
    case 'impressions': return impr > 0        ? { value: fmt(impr),                   raw: impr }     : null;
    case 'link_clicks': {
      // inline_link_clicks per Meta, all_clicks per Google Display/Video
      const linkClicks = clicks > 0 ? clicks : (parseInt(ins.all_clicks) || 0);
      return linkClicks > 0 ? { value: fmt(linkClicks), raw: linkClicks } : null;
    }
    case 'reach':       return reach > 0       ? { value: fmt(reach),                  raw: reach }    : null;
    case 'engagement':  return engagement > 0  ? { value: fmt(engagement),             raw: engagement } : null;
    case 'reactions':   return reactions > 0   ? { value: fmt(reactions),              raw: reactions }  : null;
    case 'video_views': return null; // non ancora in DB
    case 'cpm':         return impr > 0        ? { value: fmtEur(spend / impr * 1000), raw: spend / impr * 1000 } : null;
    case 'cpc':         return clicks > 0      ? { value: fmtEur(spend / clicks),      raw: spend / clicks }     : null;
    case 'ctr':         return impr > 0        ? { value: fmtPct(clicks / impr * 100), raw: clicks / impr * 100 } : null;
    case 'video_completion_rate': return ins.video_completion_rate != null
      ? { value: fmtPct(ins.video_completion_rate), raw: ins.video_completion_rate } : null;
    case 'app_installs':         return ins.app_installs > 0
      ? { value: fmt(ins.app_installs), raw: ins.app_installs } : null;
    case 'cost_per_app_install': return ins.cost_per_app_install > 0
      ? { value: fmtEur(ins.cost_per_app_install), raw: ins.cost_per_app_install } : null;
    case 'linkedin_interest_rate': return ins.linkedin_interest_rate != null
      ? { value: fmtPct(ins.linkedin_interest_rate), raw: ins.linkedin_interest_rate } : null;
    case 'linkedin_apply_clicks': return ins.linkedin_apply_clicks > 0
      ? { value: fmt(ins.linkedin_apply_clicks), raw: ins.linkedin_apply_clicks } : null;
    case 'linkedin_segments': return ins.linkedin_segments
      ? { value: ins.linkedin_segments, raw: ins.linkedin_segments } : null;
    case 'linkedin_audience': return ins.linkedin_audience
      ? { value: ins.linkedin_audience, raw: ins.linkedin_audience } : null;
    case 'ig_story_impressions': return ins.ig_story_impressions > 0
      ? { value: fmt(ins.ig_story_impressions), raw: ins.ig_story_impressions } : null;
    default: return null;
  }
}

/* ─── Channel → platform mapping ─── */
function campaignMatchesChannel(campaign, channel) {
  const platform = (campaign.platform || '').toLowerCase();
  const name     = (campaign.name     || '').toLowerCase();
  switch (channel) {
    case 'meta':
      return platform === 'meta' || platform === 'facebook' || platform === 'instagram'
          || name.includes('meta') || name.includes('facebook');
    case 'google':
      return (platform === 'google' || name.includes('google'))
          && !platform.includes('youtube') && !name.includes('youtube');
    case 'youtube':
      return platform === 'youtube' || name.includes('youtube');
    case 'tiktok':
      return platform === 'tiktok' || name.includes('tiktok');
    case 'spotify':
      return platform === 'spotify' || name.includes('spotify');
    case 'linkedin':
      return platform === 'linkedin' || name.includes('linkedin');
    case 'ig_organic':
      return false; // dati organici — non da campagne paid
    default:
      return false;
  }
}

/* ─── Aggregazione campagne per un blocco ─── */
function aggregateCampaigns(campaigns, campaignInsights, adsetFilter, adsetRows) {
  const empty = { spend:0, impressions:0, clicks:0, all_clicks:0, reach:0, post_reactions:0, inline_link_clicks:0, engagement:0 };

  if (adsetFilter && adsetRows && adsetRows.length > 0) {
    // Filtra le righe adset per nome che contiene il filtro
    const filterLower = adsetFilter.toLowerCase();
    const matching = adsetRows.filter(r => (r.adset_name || '').toLowerCase().includes(filterLower));
    if (!matching.length) return { totals: {...empty}, hasData: false };
    const totals = {...empty};
    matching.forEach(r => {
      totals.spend              += parseFloat(r.spend)||0;
      totals.impressions        += parseInt(r.impressions)||0;
      totals.clicks             += parseInt(r.inline_link_clicks)||0;
      totals.all_clicks         += parseInt(r.all_clicks)||0;
      totals.reach              += parseInt(r.reach)||0;
      totals.post_reactions     += parseInt(r.post_reactions)||0;
      totals.inline_link_clicks += parseInt(r.inline_link_clicks)||0;
      totals.engagement         += parseInt(r.engagement)||0;
    });
    return { totals, hasData: true };
  }

  // Aggregazione normale per campagna (no adset_filter)
  const totals = {...empty};
  campaigns.forEach(c => {
    const ins = campaignInsights[c.id] || {};
    totals.spend              += ins.spend              || 0;
    totals.impressions        += ins.impressions        || 0;
    totals.clicks             += ins.clicks             || 0;
    totals.all_clicks         += ins.all_clicks         || 0;
    totals.reach              += ins.reach              || 0;
    totals.post_reactions     += ins.post_reactions     || 0;
    totals.inline_link_clicks += ins.inline_link_clicks || 0;
    totals.engagement         += ins.engagement         || 0;
  });
  return { totals, hasData: campaigns.length > 0 };
}

/* ─── Date auto ─── */
function autoDateStart(campaigns) {
  const dates = campaigns.map(c => c.start_date).filter(Boolean).sort();
  if (!dates.length) return null;
  return new Date(dates[0]).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
}
function autoDateEnd(campaigns) {
  const dates = campaigns.map(c => c.end_date).filter(Boolean).sort().reverse();
  if (!dates.length) return null;
  return new Date(dates[0]).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
}

const ECONOMICS_KEYS = ['spend', 'cpm', 'cpc', 'ctr', 'cost_per_app_install'];

/* ═══════════════════════════════════════════════════════════════════════════
   TEMPLATE-AWARE RENDER
═══════════════════════════════════════════════════════════════════════════ */
function TemplateReport({ client, template, campaigns, campaignInsights, adsetRows, campaignAds, demographics, dateFrom, dateTo }) {
  const theme = getTheme(client?.name);
  const today = new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  const hideEconomics = template.hide_economics === true;
  console.log('[RT] adsetRows:', adsetRows?.length, '| campaigns:', campaigns?.length, '| template:', template?.id);

  // Periodo header
  const periodStart = autoDateStart(campaigns) ||
    new Date(dateFrom).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  const periodEnd = autoDateEnd(campaigns) ||
    new Date(dateTo).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });

  const { totals, bycamp } = aggregateCampaigns(campaigns, campaignInsights);

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", background: "#fff", minWidth: 800, color: "#1a1a2e" }}>
      <style>{"@media print{.no-print{display:none!important}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}"}</style>

      {/* HEADER */}
      <div style={{ padding: "48px 64px 32px", borderBottom: "1px solid #e5e5e5" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 600, color: "#1a1a2e", marginBottom: 4 }}>{client?.name || "Report"}</div>
            <div style={{ fontSize: 14, color: "#888" }}>{periodStart} — {periodEnd}</div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: theme.primary, background: theme.primary + "18", border: `1px solid ${theme.primary}33`, borderRadius: 8, padding: "6px 14px", marginTop: 4 }}>
            {template.label}
          </div>
        </div>
        <div style={{ display: "flex", gap: 24, fontSize: 12, color: "#999", marginTop: 16 }}>
          <span>{campaigns.length} campagn{campaigns.length === 1 ? "a" : "e"}</span>
          <span>Generato il {today}</span>
          {hideEconomics && <span style={{ color: "#f59e0b" }}>✦ No economics</span>}
        </div>
      </div>

      {/* BODY — sezioni dal template */}
      <div style={{ padding: "40px 64px" }}>

        {/* CREATIVO IN TESTA per single_image */}
        {(template.single_image || template.id === 'minitop') && (() => {
          const campsWithImg = campaigns.filter(c => campaignAds?.[c.id]?.stored_image_url || c.preview_image_url);
          if (!campsWithImg.length) return null;
          const c = campsWithImg[0];
          const ad = campaignAds?.[c.id];
          const imgUrl = ad?.stored_image_url || c.preview_image_url;
          const metaSection = template.sections.find(s => s.id === 'meta');
          const metaBlock = metaSection?.blocks[0];
          const metaCamps = campaigns.filter(cc => campaignMatchesChannel(cc, 'meta'));
          const { totals: mt } = aggregateCampaigns(metaCamps, campaignInsights, null, adsetRows);
          const kpiList = metaBlock
            ? metaBlock.kpis.filter(k =>
                k.key !== 'date_start' && k.key !== 'date_end' &&
                !(hideEconomics && ECONOMICS_KEYS.includes(k.key)) &&
                k.aggregation !== 'conditional'
              )
            : [];
          return (
            <div style={{ marginBottom: 48 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 2, marginBottom: 20, paddingBottom: 8, borderBottom: "2px solid #f0f0ee" }}>
                Creativo
              </div>
              <div style={{ display: "flex", gap: 32, alignItems: "flex-start" }}>
                {imgUrl && (
                  <div style={{ flexShrink: 0, width: "22%", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.10)", background: "#f0f0ee" }}>
                    <img src={imgUrl} alt="" style={{ width: "100%", display: "block", objectFit: "cover" }} />
                  </div>
                )}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
                  {kpiList.map(kpi => {
                    const resolved = resolveKpi(kpi.key, mt);
                    if (!resolved) return null;
                    return (
                      <div key={kpi.key} style={{ background: "#fafafa", border: "1px solid #eee", borderRadius: 10, padding: "16px 20px" }}>
                        <div style={{ fontSize: 10, color: "#999", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>{kpi.label}</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: "#1a1a2e", letterSpacing: "-0.5px" }}>{resolved.value}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {template.sections.map(section => {
          // Sezione demographics speciale
          if (section.id === 'meta_demographics') {
            const hasDemoData = demographics && (
              (demographics.gender || []).filter(g => g.gender !== "UNDETERMINED" && g.gender !== "unknown").length > 0 ||
              (demographics.age || []).length > 0
            );
            if (!hasDemoData) return null;
            return (
              <div key={section.id} style={{ marginBottom: 48 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 2, marginBottom: 20, paddingBottom: 8, borderBottom: "2px solid #f0f0ee" }}>
                  {section.title}
                </div>
                <DemographicsSection demographics={demographics} />
              </div>
            );
          }
          // Per minitop: nascondi le sezioni (KPI già mostrati nel Creativo in testa)
          // Per altri template single_image: mantieni le sezioni (breakdown dettagliato)
          if (template.id === 'minitop' && section.id !== 'meta_demographics') return null;

          // Mostra sezione se almeno un blocco ha dati
          const sectionHasAnyData = section.blocks.some(block => {
            const ch = campaigns.filter(c => campaignMatchesChannel(c, block.channel));
            if (!block.adset_filter) return ch.length > 0;
            if (!adsetRows || !adsetRows.length) return false;
            const filterLower = block.adset_filter.toLowerCase();
            return adsetRows.some(r => (r.adset_name || '').toLowerCase().includes(filterLower));
          });
          if (!sectionHasAnyData) return null;

          return (
          <div key={section.id} style={{ marginBottom: 48 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 2, marginBottom: 20, paddingBottom: 8, borderBottom: "2px solid #f0f0ee" }}>
              {section.title}
            </div>

            {section.blocks.map((block, bi) => {
              const kpis = block.kpis.filter(kpi => {
                if (hideEconomics && ECONOMICS_KEYS.includes(kpi.key)) return false;
                return true;
              });
              if (!kpis.length) return null;

              const dateKpis = kpis.filter(k => k.key === 'date_start' || k.key === 'date_end');
              const dataKpis = kpis.filter(k => k.key !== 'date_start' && k.key !== 'date_end');

              // Filtra per canale
              const channelCampaigns = campaigns.filter(c => campaignMatchesChannel(c, block.channel));
              // Per adset_filter NON filtrare per nome campagna — il filtro è per nome ADSET
              const blockCampaigns = channelCampaigns;

              if (!blockCampaigns.length && !block.adset_filter) return null;

              const { totals: bt, hasData: blockHasData } = aggregateCampaigns(blockCampaigns, campaignInsights, block.adset_filter, adsetRows);
              if (!blockHasData && block.adset_filter) return null;

              return (
                <div key={bi} style={{ marginBottom: 28, background: "#fafafa", borderRadius: 10, border: "1px solid #eee", overflow: "hidden" }}>
                  {/* Block header */}
                  {block.label && (
                    <div style={{ padding: "12px 20px", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>{block.label}</span>
                      {block.adset_filter && (
                        <span style={{ fontSize: 11, color: "#999", background: "#f0f0ee", borderRadius: 4, padding: "2px 8px" }}>
                          filtro: {block.adset_filter}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Date row */}
                  {dateKpis.length > 0 && (
                    <div style={{ padding: "10px 20px", borderBottom: dataKpis.length > 0 ? "1px solid #eee" : "none", display: "flex", gap: 24 }}>
                      {dateKpis.map(kpi => {
                        const val = kpi.key === 'date_start'
                          ? autoDateStart(blockCampaigns)
                          : autoDateEnd(blockCampaigns);
                        return (
                          <div key={kpi.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>{kpi.label}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>{val || "—"}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* KPI grid */}
                  {dataKpis.length > 0 && (
                    <>
                      {/* DISTINCT: mostra per campagna */}
                      {dataKpis.some(k => k.aggregation === 'distinct') && !block.adset_filter ? (
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                            <thead>
                              <tr style={{ background: "#f5f5f3" }}>
                                <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #eee" }}>Campagna</th>
                                {dataKpis.map(kpi => (
                                  <th key={kpi.key} style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>
                                    {kpi.label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {blockCampaigns.map((c, ci) => {
                                const ins = campaignInsights[c.id] || {};
                                return (
                                  <tr key={c.id} style={{ borderBottom: ci < blockCampaigns.length - 1 ? "1px solid #f0f0ee" : "none", background: ci % 2 === 0 ? "#fff" : "#fafafa" }}>
                                    <td style={{ padding: "10px 16px", color: "#333", fontWeight: 500, fontSize: 12, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {c.name}
                                    </td>
                                    {dataKpis.map(kpi => {
                                      const resolved = resolveKpi(kpi.key, ins);
                                      const isEmpty = !resolved && kpi.aggregation === 'conditional';
                                      return (
                                        <td key={kpi.key} style={{ padding: "10px 16px", textAlign: "right", fontWeight: 600, color: resolved ? "#1a1a2e" : "#ccc", fontVariantNumeric: "tabular-nums" }}>
                                          {isEmpty ? "—" : resolved ? resolved.value : "—"}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                              {/* Riga totali SUM */}
                              {dataKpis.some(k => k.aggregation === 'sum') && blockCampaigns.length > 1 && (
                                <tr style={{ borderTop: "2px solid #e5e5e5", background: "#f5f5f3", fontWeight: 700 }}>
                                  <td style={{ padding: "10px 16px", fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Totale</td>
                                  {dataKpis.map(kpi => {
                                    if (kpi.aggregation !== 'sum') return <td key={kpi.key} style={{ padding: "10px 16px" }} />;
                                    const resolved = resolveKpi(kpi.key, bt);
                                    return (
                                      <td key={kpi.key} style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, color: "#1a1a2e", fontVariantNumeric: "tabular-nums" }}>
                                        {resolved ? resolved.value : "—"}
                                      </td>
                                    );
                                  })}
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        /* SUM/CONDITIONAL: griglia KPI aggregati */
                        <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(dataKpis.length, 5)}, 1fr)`, gap: 0 }}>
                          {dataKpis.map((kpi, ki) => {
                            const resolved = resolveKpi(kpi.key, bt);
                            const isEmpty = !resolved && kpi.aggregation === 'conditional';
                            if (isEmpty) return null;
                            return (
                              <div key={kpi.key} style={{
                                padding: "20px 24px",
                                borderRight: ki < dataKpis.length - 1 ? "1px solid #eee" : "none",
                                borderTop: "1px solid #eee",
                              }}>
                                <div style={{ fontSize: 10, color: "#999", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
                                  {kpi.label}
                                  {kpi.note && <span style={{ fontSize: 9, color: "#bbb", marginLeft: 4, fontWeight: 400 }}>({kpi.note})</span>}
                                </div>
                                <div style={{ fontSize: 20, fontWeight: 600, color: resolved ? "#1a1a2e" : "#ddd" }}>
                                  {resolved ? resolved.value : "—"}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        );
        })}

        {/* CREATIVI — solo per template NON single_image */}
        {!(template.single_image || template.id === 'minitop') && (() => {
          const campsWithImg = campaigns.filter(c => campaignAds?.[c.id]?.stored_image_url || c.preview_image_url);
          if (!campsWithImg.length) return null;
          const displayCamps = campsWithImg;
          return (
            <div style={{ marginBottom: 48 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 2, marginBottom: 20, paddingBottom: 8, borderBottom: "2px solid #f0f0ee" }}>
                Creativo
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {displayCamps.map(c => {
                  const ad = campaignAds?.[c.id];
                  const imgUrl = ad?.stored_image_url || c.preview_image_url;
                  // KPI principali della sezione Meta (primo blocco del template)
                  const metaSection = template.sections.find(s => s.id === 'meta');
                  const metaBlock = metaSection?.blocks[0];
                  const metaCamps = campaigns.filter(cc => campaignMatchesChannel(cc, 'meta'));
                  const { totals: mt } = aggregateCampaigns(metaCamps, campaignInsights, null);
                  const kpiList = metaBlock
                    ? metaBlock.kpis.filter(k =>
                        k.key !== 'date_start' && k.key !== 'date_end' &&
                        !(hideEconomics && ECONOMICS_KEYS.includes(k.key)) &&
                        k.aggregation !== 'conditional'
                      )
                    : [];
                  return (
                    <div key={c.id} style={{ display: "flex", gap: 32, alignItems: "flex-start" }}>
                      {/* Immagine */}
                      {imgUrl && (
                        <div style={{
                          flexShrink: 0,
                          width: "22%",
                          borderRadius: 12,
                          overflow: "hidden",
                          boxShadow: "0 2px 12px rgba(0,0,0,0.10)",
                          background: "#f0f0ee",
                        }}>
                          <img
                            src={imgUrl}
                            alt=""
                            style={{ width: "100%", display: "block", objectFit: "cover" }}
                          />
                        </div>
                      )}
                      {/* KPI a destra */}
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
                        {kpiList.map(kpi => {
                          const resolved = resolveKpi(kpi.key, mt);
                          if (!resolved) return null;
                          return (
                            <div key={kpi.key} style={{
                              background: "#fafafa",
                              border: "1px solid #eee",
                              borderRadius: 10,
                              padding: "16px 20px",
                            }}>
                              <div style={{ fontSize: 10, color: "#999", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                                {kpi.label}
                              </div>
                              <div style={{ fontSize: 24, fontWeight: 700, color: "#1a1a2e", letterSpacing: "-0.5px" }}>
                                {resolved.value}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* FOOTER */}
        <div style={{ borderTop: "1px solid #e5e5e5", paddingTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 11, color: "#999" }}>Gallicantus Italia S.r.l.</div>
          <div style={{ fontSize: 11, color: "#999" }}>{today}</div>
        </div>
      </div>
    </div>
  );
}

/* ─── Sezione demografici ─── */
function DemographicsSection({ demographics }) {
  const demo = demographics || { gender: [], age: [] };
  const genderLabelsMap  = { MALE: "Maschi", FEMALE: "Femmine", male: "Maschi", female: "Femmine", Maschi: "Maschi", Femmine: "Femmine" };
  const genderColorsMap  = { MALE: "#5BC4F5", FEMALE: "#F5A0C8", male: "#5BC4F5", female: "#F5A0C8", Maschi: "#5BC4F5", Femmine: "#F5A0C8" };
  const ageLabels = { AGE_RANGE_18_24: "18-24", AGE_RANGE_25_34: "25-34", AGE_RANGE_35_44: "35-44", AGE_RANGE_45_54: "45-54", AGE_RANGE_55_64: "55-64", AGE_RANGE_65_UP: "65+" };

  const allGender = (demo.gender || []).filter(g => g.gender !== "UNDETERMINED" && g.gender !== "unknown");
  const metaGender   = allGender.filter(g => ["male", "female"].includes(g.gender));
  const googleGender = allGender.filter(g => ["Maschi", "Femmine"].includes(g.gender));
  const ageData = demo.age || [];
  const maxAge = ageData.length > 0 ? Math.max(...ageData.map(a => a.clicks), 1) : 1;

  if (!metaGender.length && !googleGender.length && !ageData.length) return null;

  return (
    <div style={{ marginBottom: 48 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 2, marginBottom: 20, paddingBottom: 8, borderBottom: "2px solid #f0f0ee" }}>
        Demografico Target — Meta
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {[metaGender, googleGender].filter(g => g.length > 0).map((gArr, gi) => {
            const total = gArr.reduce((s, g) => s + g.impressions, 0);
            return (
              <div key={gi} style={{ background: "#f5f5f3", borderRadius: 8, padding: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 16 }}>Genere — Impressioni</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {gArr.map(g => {
                    const pct = total > 0 ? (g.impressions / total) * 100 : 0;
                    return (
                      <div key={g.gender}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>{genderLabelsMap[g.gender] || g.gender}</span>
                          <span style={{ fontSize: 12, color: "#888" }}>{pct.toLocaleString("it-IT", { maximumFractionDigits: 1 })}% · {(g.impressions || 0).toLocaleString("it-IT")} impr</span>
                        </div>
                        <div style={{ background: "#e5e5e5", borderRadius: 4, height: 8, overflow: "hidden" }}>
                          <div style={{ height: "100%", borderRadius: 4, background: genderColorsMap[g.gender] || "#ccc", width: pct + "%" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {ageData.length > 0 && (
          <div style={{ background: "#f5f5f3", borderRadius: 8, padding: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 16 }}>Fascia d'età — Impression</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {ageData.map(a => {
                const pct = a.impressions / (Math.max(...ageData.map(x => x.impressions), 1)) * 100;
                return (
                  <div key={a.age_range}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#1a1a2e" }}>{ageLabels[a.age_range] || a.age_range}</span>
                      <span style={{ fontSize: 11, color: "#888" }}>{(a.impressions || 0).toLocaleString("it-IT")} impr</span>
                    </div>
                    <div style={{ background: "#e5e5e5", borderRadius: 4, height: 8, overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 4, background: "#7F77DD", width: pct + "%", minWidth: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FALLBACK — render legacy (nessun template selezionato)
═══════════════════════════════════════════════════════════════════════════ */
function LegacyReport({ client, dateFrom, dateTo, campaigns, campaignInsights, campaignAds, demographics, mockupImageUrl, aiSummary }) {
  const theme = getTheme(client?.name);
  const totalSpend  = campaigns.reduce((s, c) => s + (campaignInsights[c.id]?.spend       || 0), 0);
  const totalImpr   = campaigns.reduce((s, c) => s + (campaignInsights[c.id]?.impressions || 0), 0);
  const totalClicks = campaigns.reduce((s, c) => s + (campaignInsights[c.id]?.clicks      || 0), 0);
  const ctr = totalImpr > 0 ? ((totalClicks / totalImpr) * 100).toLocaleString("it-IT", { minimumFractionDigits: 2 }) + "%" : "0,00%";
  const top = [...campaigns].sort((a, b) => (campaignInsights[b.id]?.spend || 0) - (campaignInsights[a.id]?.spend || 0));
  const today = new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  const df = new Date(dateFrom).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  const dt = new Date(dateTo).toLocaleDateString("it-IT",   { day: "2-digit", month: "long", year: "numeric" });
  const demo = demographics || { gender: [], age: [] };
  const allGender  = (demo.gender || []).filter(g => g.gender !== "UNDETERMINED" && g.gender !== "unknown");
  const metaGender = allGender.filter(g => ["male","female"].includes(g.gender));
  const googleGender = allGender.filter(g => ["Maschi","Femmine"].includes(g.gender));
  const ageData = demo.age || [];
  const ageLabels = { AGE_RANGE_18_24:"18-24", AGE_RANGE_25_34:"25-34", AGE_RANGE_35_44:"35-44", AGE_RANGE_45_54:"45-54", AGE_RANGE_55_64:"55-64", AGE_RANGE_65_UP:"65+" };
  const genderLabelsMap = { MALE:"Maschi", FEMALE:"Femmine", male:"Maschi", female:"Femmine", Maschi:"Maschi", Femmine:"Femmine" };
  const genderColorsMap = { MALE:"#5BC4F5", FEMALE:"#F5A0C8", male:"#5BC4F5", female:"#F5A0C8", Maschi:"#5BC4F5", Femmine:"#F5A0C8" };
  const maxAge = ageData.length > 0 ? Math.max(...ageData.map(a => a.clicks), 1) : 1;
  const hasDemographics = metaGender.length > 0 || googleGender.length > 0 || ageData.length > 0;
  const maxSpend = top.length > 0 ? Math.max(...top.map(c => campaignInsights[c.id]?.spend || 0), 1) : 1;
  const maxImpr  = top.length > 0 ? Math.max(...top.map(c => campaignInsights[c.id]?.impressions || 0), 1) : 1;
  const periodLabel = df + " — " + dt;

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", background: "#fff", minWidth: 800, color: "#1a1a2e" }}>
      <style>{"@media print{.no-print{display:none!important}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}"}</style>
      <div style={{ padding: "48px 64px 32px", borderBottom: "1px solid #e5e5e5" }}>
        <div style={{ fontSize: 28, fontWeight: 500, color: "#1a1a2e", marginBottom: 6 }}>{client?.name || "Report"}</div>
        <div style={{ fontSize: 14, color: "#888", marginBottom: 20 }}>{periodLabel}</div>
        <div style={{ display: "flex", gap: 24, fontSize: 12, color: "#999" }}>
          <span>{campaigns.length} campagn{campaigns.length === 1 ? "a" : "e"}</span>
          <span>Generato il {today}</span>
        </div>
      </div>
      <div style={{ padding: "40px 64px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 48 }}>
          {[{ label: "Importo speso", value: fmtEur(totalSpend) }, { label: "Impression", value: fmt(totalImpr) }, { label: "Clic", value: fmt(totalClicks) }, { label: "CTR", value: ctr }]
            .map((k, i) => (
              <div key={i} style={{ background: "#f5f5f3", borderRadius: 8, padding: "20px 24px" }}>
                <div style={{ fontSize: 11, color: "#888", fontWeight: 500, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 500, color: "#1a1a2e" }}>{k.value}</div>
              </div>
            ))}
        </div>
        <div style={{ marginBottom: 48 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 2, marginBottom: 16 }}>Campagne</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {top.map(c => {
              const ins = campaignInsights[c.id] || {};
              const cpm = ins.impressions > 0 ? fmtEur(ins.spend / ins.impressions * 1000) : fmtEur(0);
              const platLabel = getPlatformLabel(c.name);
              const platColor = getPlatformColor(c.name);
              const ad = (campaignAds || {})[c.id];
              return (
                <div key={c.id} style={{ border: "1px solid #e5e5e5", borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
                    {c.preview_image_url
                      ? <img src={c.preview_image_url} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
                      : <div style={{ width: 48, height: 48, borderRadius: 6, background: platColor, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#fff", fontSize: 16, fontWeight: 600 }}>{(c.name || "C").charAt(0).toUpperCase()}</span></div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                      {c.start_date && <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{new Date(c.start_date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })} — {c.end_date ? new Date(c.end_date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "in corso"}</div>}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: platColor, background: platColor + "14", padding: "4px 10px", borderRadius: 4, flexShrink: 0 }}>{platLabel}</div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", borderTop: "1px solid #e5e5e5" }}>
                    {[{ label: "Spend", value: fmtEur(ins.spend) }, { label: "Impression", value: fmt(ins.impressions) }, { label: "Clic", value: fmt(ins.clicks) }, { label: "CTR", value: ins.impressions > 0 ? ((ins.clicks/ins.impressions)*100).toLocaleString("it-IT",{minimumFractionDigits:2})+"%" : "0%" }, { label: "CPM", value: cpm }]
                      .map((m, mi) => (
                        <div key={mi} style={{ padding: "12px 16px", borderRight: mi < 4 ? "1px solid #e5e5e5" : "none", textAlign: "center" }}>
                          <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{m.label}</div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>{m.value}</div>
                        </div>
                      ))}
                  </div>
                  {ad && ad.stored_image_url && (
                    <div style={{ borderTop: "1px solid #e5e5e5", padding: 20 }}>
                      <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Creativo principale</div>
                      {ad.title && <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", marginBottom: 6 }}>{ad.title}</div>}
                      {ad.body && <div style={{ fontSize: 12, color: "#555", lineHeight: 1.5, marginBottom: 12 }}>{ad.body.length > 200 ? ad.body.slice(0, 200) + "..." : ad.body}</div>}
                      <img src={ad.stored_image_url} alt="" style={{ width: "100%", maxWidth: 480, borderRadius: 10, display: "block" }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {hasDemographics && <DemographicsSection demographics={demographics} />}
        {aiSummary && (
          <div style={{ background: "#f5f5f3", borderRadius: 8, padding: "24px 28px", marginBottom: 48 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#7F77DD" }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Analisi generata da Claude AI</span>
            </div>
            <div style={{ fontSize: 13, color: "#444", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{aiSummary}</div>
          </div>
        )}
        <div style={{ borderTop: "1px solid #e5e5e5", paddingTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 11, color: "#999" }}>Gallicantus Italia S.r.l.</div>
          <div style={{ fontSize: 11, color: "#999" }}>{today}</div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   EXPORT PRINCIPALE — router template/legacy
═══════════════════════════════════════════════════════════════════════════ */
export default function ReportTemplate(props) {
  if (props.template?.component) {
    const CustomComponent = props.template.component;
    return <CustomComponent {...props} />;
  }
  if (props.template) {
    return <TemplateReport {...props} adsetRows={props.adsetRows} />;
  }
  return <LegacyReport {...props} />;
}
